<script setup lang="ts">
    import type { Schemas } from '@shopware/api-client/api-types';

    const { accountBasePath } = useAccountBasePath();
    const { logout } = useLoginState();
    const { t } = useI18n();
    const { data: story } = useConfig();

    async function invokeLogout() {
        // put cart into session storage
        const { cart } = useCart();
        const itemsData = cart.value?.lineItems?.map((lineItem: Schemas['LineItem']) => ({
            id: lineItem.id,
            referencedId: lineItem.referencedId,
            quantity: lineItem.quantity,
            type: lineItem.type
        }));
        sessionStorage.setItem('shopware-preLogoutCard', JSON.stringify(itemsData));
        logout();
    }

    const selfServiceUrl = useSelfServiceUrl();
    const selfServiceUrlAddresses = useSelfServiceUrl('addresses');
</script>
<template>
    <aside
        class="account-navigation"
        aria-label="Sidebar"
    >
        <div class="navigation-links">
            <h1>
                {{ t('account.menu.myAccount') }}
            </h1>
            <ul>
                <li>
                    <NuxtLink
                        class="navigation-link"
                        :to="accountBasePath"
                    >
                        <img
                            class="icon"
                            src="~/assets/icons/layout-dashboard.svg"
                            alt="Dashboard icon"
                        />
                        <span> {{ t('account.menu.accountOverviewHeader') }}</span>
                    </NuxtLink>
                </li>
                <li>
                    <NuxtLink
                        class="navigation-link --external-link"
                        :to="selfServiceUrl"
                    >
                        <div>
                            <img
                                class="icon"
                                src="~/assets/icons/user-round.svg"
                                alt="Profile icon"
                            />
                            <span>{{ t('account.yourProfile') }}</span>
                        </div>

                        <img
                            class="icon"
                            src="~/assets/icons/chevron-right-black.svg"
                            alt="External link icon"
                        />
                    </NuxtLink>
                </li>
                <li>
                    <NuxtLink
                        class="navigation-link --external-link"
                        :to="selfServiceUrlAddresses"
                    >
                        <div>
                            <img
                                class="icon"
                                src="~/assets/icons/home.svg"
                                alt="Addresses icon"
                            />
                            <span>{{ t('account.yourAddress') }}</span>
                        </div>

                        <img
                            class="icon"
                            src="~/assets/icons/chevron-right-black.svg"
                            alt="External link icon"
                        />
                    </NuxtLink>
                </li>
                <li v-show="!story?.content?.header?.[0]?.hideCart">
                    <NuxtLink
                        class="navigation-link"
                        :to="`${accountBasePath}/order`"
                    >
                        <img
                            class="icon"
                            src="~/assets/icons/book-user.svg"
                            alt="Order history icon"
                        />
                        <span>{{ t('account.orderHistoryHeader') }}</span>
                    </NuxtLink>
                </li>

                <li>
                    <NuxtLink
                        class="navigation-link"
                        :to="`${accountBasePath}/bonus`"
                    >
                        <img
                            class="icon"
                            src="~/assets/icons/star.svg"
                            alt=""
                        />
                        <span>{{ t('account.bonus.title') }}</span>
                    </NuxtLink>
                </li>
            </ul>

            <button
                class="logout navigation-link"
                @click="invokeLogout()"
            >
                <img
                    class="icon"
                    src="~/assets/icons/log-out.svg"
                    alt="Logout icon"
                />
                <span>{{ t('account.logout') }}</span>
            </button>
        </div>
    </aside>
</template>

<style lang="scss" scoped>
    .navigation-links {
        border: 1px solid $form-gray;
        border-radius: $border-radius-s;
        overflow: hidden;
        padding: $spacing 0 0 0;

        h1 {
            font-size: $font-m;
            font-weight: 700;
            line-height: 1;
            padding: $spacing--05 $spacing;
        }

        ul {
            list-style: none;
        }
    }

    .navigation-link {
        align-items: center;
        background-color: $white;
        display: flex;
        gap: $spacing--05;
        padding: $spacing;
        transition: background-color 0.6s $anim-ease;
        width: 100%;

        &:hover {
            background-color: $form-gray-light;
        }

        &.--external-link {
            justify-content: space-between;

            div {
                display: flex;
                gap: $spacing--05;
            }
        }

        &.link-active,
        &.link-exact-active {
            span {
                color: $brand-primary;
            }
        }

        &:focus-visible {
            box-shadow: inset 0 0 0 4px $color-focus;
        }

        &.logout {
            background-color: $form-gray-light;
            &:hover {
                background-color: $form-gray;
            }
        }
    }
</style>
